import { createTheme, Theme } from '@mui/material/styles';
import { typography } from '@/theme/typography';
import { secondaryColors } from '@/theme/colors';
import { breakPoints } from '@/theme/breakpoints';

// MUI v9 only applies the Typography `color` prop for palette entries that
// expose `.main` (e.g. primary/error/petrol) plus the `text*` tokens. Earlier
// versions also accepted custom-palette paths (`customSecondary.neutral.100`,
// `white.100`) and raw CSS color values (`#E6E7F0`, `rgb(...)`). This restores
// both so existing `color="..."`/`color={hex}` usages render as before.
const resolveTypographyColor = (palette: Theme['palette'], color?: unknown): string | undefined => {
  if (typeof color !== 'string' || color === '' || color === 'inherit') return undefined;
  // Tokens handled natively by MUI v9 - leave them to the component.
  if (color === 'textPrimary' || color === 'textSecondary' || color === 'textDisabled') {
    return undefined;
  }
  const topLevel = (palette as unknown as Record<string, unknown>)[color];
  if (topLevel && typeof topLevel === 'object' && 'main' in topLevel) {
    return undefined;
  }
  // Custom palette path, e.g. `customSecondary.neutral.100` or `white.100`.
  if (color.includes('.')) {
    const resolved = color
      .split('.')
      .reduce<unknown>(
        (acc, key) =>
          acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined,
        palette,
      );
    return typeof resolved === 'string' ? resolved : undefined;
  }
  // Raw CSS color value (#hex, rgb(), hsl(), named color).
  return color;
};

const globalThemePalette = {
  palette: {
    DataGrid: {
      bg: 'transparent',
      headerBg: '#101014',
      pinnedBg: '#499331',
    },
    petrol: {
      main: secondaryColors.primary.DEFAULT,
      contrastText: secondaryColors.primary.foreground,
      dark: secondaryColors.primary.hover,
      light: secondaryColors.primary.subtle,
    },
    primary: {
      main: '#5D51B8',
      dark: '#493EA1',
    },
    secondary: {
      main: '#1B1B1E',
    },
    tertiary: {
      main: '#FFFFFF',
      dark: '#F5F5F5',
      contrastText: '#FFFFFF',
    },
    darkBlueAndDarker: {
      main: '#26284B',
      dark: '#26284B80',
      contrastText: '#FFFFFF',
    },
    grayAndDarker: {
      main: '#49494C',
      dark: '#49494C80',
      contrastText: '#FFFFFF',
    },
    admin: {
      main: '#58585B',
      dark: '#5D51B8',
      contrastText: '#FFFFFF',
    },
    white: {
      100: '#FFFFFF',
      90: '#FFFFFFE6',
      80: '#FFFFFFCC',
      70: '#FFFFFFB3',
      50: '#FFFFFF80',
    },
    customSecondary: secondaryColors,
  },
};

export const theme = createTheme({
  components: {
    MuiTypography: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const color = resolveTypographyColor(theme.palette, ownerState.color);
          return color ? { color } : {};
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'admin' },
          style: {
            padding: '6px 8px',
            border: '1px solid black',
            borderRadius: 0,
            width: '289px',
            height: '63px',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'capitalize',
            color: globalThemePalette.palette.admin.contrastText,
            backgroundColor: globalThemePalette.palette.admin.main,

            '&:hover': {
              backgroundColor: globalThemePalette.palette.admin.dark,
            },
          },
        },
        {
          props: { variant: 'contained', color: 'petrol' },
          style: {
            backgroundColor: secondaryColors.primary.DEFAULT,
            color: secondaryColors.primary.foreground,
            '&:hover': {
              backgroundColor: secondaryColors.primary.hover,
            },
            '&:active': {
              backgroundColor: secondaryColors.primary.active,
            },
            '&.Mui-disabled': {
              backgroundColor: secondaryColors.neutral[400],
              color: secondaryColors.neutral[300],
            },
          },
        },
        // TODO: after switch to the new segment, remove old theme and replace petrol with primary
        // {
        //   props: { variant: 'contained', color: 'primary' },
        //   style: {
        //     backgroundColor: secondaryColors.primary.DEFAULT,
        //     color: secondaryColors.neutral[100],
        //     '&:hover': {
        //       backgroundColor: secondaryColors.primary.hover,
        //     },
        //     '&:active': {
        //       backgroundColor: secondaryColors.primary.active,
        //     },
        //   },
        // },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            backgroundColor: secondaryColors.neutral[400],
            color: secondaryColors.neutral[100],
            '&:hover': {
              backgroundColor: secondaryColors.background.raised,
            },
            '&:active': {
              backgroundColor: secondaryColors.neutral[600],
            },
          },
        },
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            backgroundColor: secondaryColors.danger.DEFAULT,
            color: secondaryColors.primary.foreground,
            '&:hover': {
              backgroundColor: secondaryColors.danger.numeric,
            },
            '&:active': {
              backgroundColor: secondaryColors.danger.DEFAULT,
            },
            '&.Mui-disabled': {
              backgroundColor: `${secondaryColors.danger.DEFAULT}80`,
              color: secondaryColors.neutral[200],
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          // correct values by default:
          // fontWeight: 500,
          // borderRadius: "4px",
          //______________________________________________
          padding: '6px 14px',
          width: '100%',
          minWidth: 0,
          height: 'auto',
          fontFamily: 'General Sans, sans-serif',
          fontStyle: 'normal',
          fontSize: '14px',
          lineHeight: '150%',
          letterSpacing: '0.15px',
          textTransform: 'none',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: globalThemePalette.palette.white['100'],
        },
      },
    },
    MuiRadio: {
      variants: [
        {
          props: { color: 'petrol' },
          style: {
            color: secondaryColors.primary.subtle,
            '&.Mui-checked': {
              color: secondaryColors.primary.DEFAULT,
            },
          },
        },
        {
          props: { color: 'primary' },
          style: {
            color: secondaryColors.neutral[300],
            padding: '4px',
            '&.Mui-checked': {
              color: secondaryColors.primary.subtle,
            },
          },
        },
      ],
    },
    MuiCheckbox: {
      variants: [
        {
          props: { color: 'petrol' },
          style: {
            color: secondaryColors.primary.subtle,

            '& .MuiSvgIcon-root': {
              color: secondaryColors.primary.subtle,
            },

            '&.Mui-checked': {
              color: secondaryColors.primary.DEFAULT,
            },
            '&.Mui-checked .MuiSvgIcon-root': {
              color: secondaryColors.primary.DEFAULT,
            },

            '&.MuiCheckbox-indeterminate': {
              color: secondaryColors.primary.DEFAULT,
            },

            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
      ],
    },
    MuiChip: {
      variants: [
        {
          props: { variant: 'petrol' },
          style: {
            // outlined-like style
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: secondaryColors.primary.DEFAULT,
            color: secondaryColors.primary.DEFAULT,
            borderRadius: 999,
            fontWeight: 500,

            // hover style
            '&:hover': {
              backgroundColor: `${secondaryColors.primary.DEFAULT}14`, // ~8% alpha
              borderColor: secondaryColors.primary.hover,
            },
          },
        },
      ],
    },
  },
  typography: {
    fontFamily: 'General Sans, sans-serif',
    h1: { ...typography.AgioH1 },
    h2: {
      fontSize: '28px',
    },
    custom: typography,
  },
  ...globalThemePalette,
  breakPoints,
});
